/* eslint-disable camelcase */
/* eslint-disable react/prop-types */
/** @jsxImportSource theme-ui */
import { jsx } from 'theme-ui';
import React from 'react';
import { Grid, Box, Heading, Flex } from '@theme-ui/components';
import styled from '@emotion/styled';
import { Link } from 'gatsby';
import { getImage } from 'gatsby-plugin-image';
import { convertToBgImage } from 'gbimage-bridge';
import BackgroundImage from 'gatsby-background-image';
import { useBreakpointIndex } from '../../utils/media-query';
import { colors, typography } from '../../styles/theme';
import { Chip, CustomLink } from '../atoms';
import { SelectionCard } from '../molecules';
import themeStyles from "../../gatsby-plugin-theme-ui/index";

const StateHolidayListLinkWrapper = styled.div`
  padding: 1.5rem;
  border: 1px solid ${colors.neutral200};
  border-radius: 20px;
`;

export const PillarLink = ({ item: { name, slug } }) => (
	<AnyLink
		prefix='/wiki/'
		name={name}
		slug={slug}
	/>
);

export const StateLink = ({ item: { name, slug } }) => (
	<AnyLink
		prefix='/wiki/acts/st-'
		name={name}
		slug={slug}
	/>
);

export const StateHeadingLink = ({ item: { name, slug } }) => (
	<Link to={`/wiki/acts/st-${slug}/`}>
			{name}
		</Link>
);

export const StateHolidayListLink = ({
	item: { name, slug },
	isCustomLink,
	holidayYear,
  }) => {
	const prefix = "/wiki/acts/st-";
	const suffix = `/holidays/${holidayYear}`;
	const linkComponent = (
	  <AnyLink
		prefix={prefix}
		name={name}
		slug={slug}
		suffix={suffix}
	  />
	);
	return isCustomLink ? (
		linkComponent
	) : (
	  <StateHolidayListLinkWrapper>
		{linkComponent}
	  </StateHolidayListLinkWrapper>
	);
  };
  

export const StateProfTaxLink = ({ item: { name, slug } }) => (
	<SelectionLink
		prefix='/wiki/acts/professional-tax-'
		name={name}
		slug={slug}
		suffix=''
	/>
);

export const ActLink = ({ item: { name, slug } }) => (
	<AnyLink
		prefix='/wiki/acts/'
		name={name}
		slug={slug}
	/>
);
export const StateShopsLink = ({ item: { name, slug } }) => (
	<SelectionLink
		prefix='/wiki/acts/'
		name={name}
		slug={slug}
		suffix='-shops-commercial-establishments-act'
	/>
);

export const StateLwfLink = ({ item: { name, slug } }) => (
	<SelectionLink
		prefix='/wiki/acts/'
		name={name}
		slug={slug}
		suffix='-labour-welfare-fund-act'
	/>
);

export const IndustryLink = ({ item: { name, slug } }) => (
	<AnyLink
		prefix='/wiki/acts/in-'
		name={name}
		slug={slug}
	/>
);

export const StateCowLink = ({ item: { name, slug } }) => (
	<SelectionLink
		prefix='/wiki/acts/'
		name={name}
		slug={slug}
		suffix='-code-on-wages-rules-2021'
	/>
);

export const StateIrcLink = ({ item: { name, slug } }) => (
	<SelectionLink
		prefix='/wiki/acts/'
		name={name}
		slug={slug}
		suffix='-industrial-relations-code-rules-2021'
	/>
);

export const StateCossLink = ({ item: { name, slug } }) => (
	<SelectionLink
		prefix='/wiki/acts/'
		name={name}
		slug={slug}
		suffix='-code-on-social-security-rules-2021'
	/>
);

export const StateOshwcLink = ({ item: { name, slug } }) => (
	<SelectionLink
		prefix='/wiki/acts/'
		name={name}
		slug={slug}
		suffix='-occupational-safety-health-and-working-conditions-code-rules-2021'
	/>
);

export const CalendarLink = ({
	item: { name, slug },
	isButtonLink = false,
}) => (
	<AnyLink
		prefix='/wiki/'
		name={name}
		slug={slug}
		suffix='-compliance-calendar'
		isButtonLink={isButtonLink}
	/>
);

export const CompliancesLink = ({ item: { name, slug }, isCustomLink }) =>
	isCustomLink ? (
		<CustomLink url={`/wiki/compliances/${slug}/`}>{name}</CustomLink>
	) : (
		<SelectionLink
			prefix="/wiki/compliances/"
			name={name}
			slug={slug}
		/>
	);

const SelectionLink = ({ prefix, name, slug, suffix = '', sx }) => {
	return (
		<CustomLink url={`${prefix}${slug}${suffix}/`}>
			<SelectionCard
				title={name}
				titleColor={colors.primaryMain}
				showRightIcon
				customStyles={{ cursor: 'pointer', ...sx}}
			/>
		</CustomLink>
	);
};

const AnyLink = ({ prefix, name, slug, suffix = '', isButtonLink = false }) => {
	if (isButtonLink) {
		return <ButtonLink to={`${prefix}${slug}${suffix}/`}>{name}</ButtonLink>;
	} else {
		return <CustomLink url={`${prefix}${slug}${suffix}/`}>{name}</CustomLink>;
	}
};

const ButtonLink = styled(Link)`
	padding: 4px 10px;
	background: linear-gradient(
		to bottom,
		${(props) => props.theme.colors.primary} 0%,
		${(props) => props.theme.colors.primary} 100%
	);
	background-position: 0 100%;
	background-repeat: repeat-x;
	background-size: 2px 2px;
	text-decoration: none;
	transition: background-size 0.2s;
	&:hover {
		background-size: 4px 50px;
		color: white;
	}
`;

const SimpleCrumb = ({ crumb, first=false, last=false }) => (
  <Box
    sx={{
      color: colors.black,
      fontSize: '0.9rem',
      userSelect: 'none',
      '& a': {
        color: colors.black,
        textDecoration: 'none',
        ...typography.textMd400,
      },
      '& a:hover': {
        textDecoration: 'underline',
      },
    }}
  >
    <Link to={crumb.path}>{crumb.crumbLabel}</Link>
    {!last && <span sx={{ mx: '0.313rem' }}>→</span>}
  </Box>
)

const PillarCrumb = ({ crumb }) => (
	<Link to={crumb.path}>
		<Chip
			label={crumb.crumbLabel}
			type='subtle'
		/>
	</Link>
);

export const Crumbs = ({ crumbs, variant = 'pillar' }) => {
	const count = crumbs.length;
	const justifyContent = variant === 'pillar' ? 'center' : 'flex-start';
	return (
		<Flex sx={{ justifyContent, flexWrap: 'wrap', gap: '1rem' }}>
			{crumbs.map((crumb, index) => {
				return (
					<Box>
						{variant === 'pillar' && (
							<PillarCrumb
								key={`crumb-${index}`}
								crumb={crumb}
								first={index === 0}
								last={index === count - 1}
							/>
						)}
						{variant === 'simple' && (
							<SimpleCrumb
								key={`crumb-${index}`}
								crumb={crumb}
								first={index === 0}
								last={index === count - 1}
							/>
						)}
					</Box>
				);
			})}
		</Flex>
	);
};

export const HeroSection = ({ image, styles = {}, children }) => {
	const minHeight = '180px';

	if (image && image.length > 0) {
		const bgImage = convertToBgImage(getImage(image));

		return (
			<BackgroundSection
				id='stateImg'
				styles={styles}
				className='stateImg'
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					mb: 0,
					p: 4,
					backgroundColor: 'primary',
					minHeight,
				}}
				bgImage={bgImage}
			>
				{children}
			</BackgroundSection>
		);
	} else {
		return <Box sx={styles}>{children}</Box>;
	}
};

export const PageHeading = ({
	pre,
	title,
	subhead,
	image,
	crumbs,
	styles = {},
}) => {
	const minHeight = '180px';

	if (image) {
		const bgImage = convertToBgImage(getImage(image));
		return (
			<BackgroundSection
				id='stateImg'
				styles={styles}
				className='stateImg'
				sx={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'center',
					mb: 0,
					py: 4,
					px: [3, 3, 4, "9rem"],
					backgroundColor: colors.secondaryBlueMain,
					minHeight,
				}}
				bgImage={bgImage}
			>
				<PageHeading2
					pre={pre}
					title={title}
					subhead={subhead}
					crumbs={crumbs}
				/>
			</BackgroundSection>
		);
	}
	return (
		<Box
			style={styles}
			sx={{
				display: 'flex',
				alignItems: 'center',
				justifyContent: 'center',
				py: 4,
				px: [3, 3, 4, "9rem"],
				backgroundColor: colors.secondaryBlueMain,
				minHeight,
			}}
		>
			<PageHeading2
				pre={pre}
				title={title}
				subhead={subhead}
				crumbs={crumbs}
			/>
		</Box>
	);
};

export const PageHeading2 = ({
	pre,
	title,
	subhead,
	crumbs,
	align = 'left',
}) => (
	<Box
		sx={{ textAlign: 'center', border: '0px solid red', pt: 4 }}
	>
		{crumbs ? <Crumbs crumbs={crumbs} /> : null}
		{pre ? (
			<Box
				sx={{
					display: 'block',
					my: ['0.813rem'],
					...typography.textMd400,
				}}
			>
				{pre}
			</Box>
		) : null}
		<Heading
			sx={{ my: ['0.813rem'], ...typography.h1 }}
			as='h1'
		>
			<div dangerouslySetInnerHTML={{ __html: title }} />
		</Heading>
		{subhead ? (
			<Box
				sx={{
					mt: 3,
					mb: 3,
					...typography.textMd400,
				}}
			>
				<div dangerouslySetInnerHTML={{ __html: subhead }} />
			</Box>
		) : null}
	</Box>
);

export const RichContent = ({
	title,
	body,
	variant = 'wikiHeading2',
	styles = {},
	as = 'h2',
}) => {
	if (
		body === undefined ||
		body === null ||
		body === '' ||
		body === '<p></p>\n'
	) {
		return null;
	}
	return (
		<Box sx={{...themeStyles.styles}}>
			{title ? (
				<Heading
					as={as}
					sx={{
						...typography[as]
					}}
				>
					{title}
				</Heading>
			) : null}
			<Box
				px={1}
				sx={styles}
				dangerouslySetInnerHTML={{ __html: body }}
			/>
		</Box>
	);
};

export const ItemListContainer = styled(Box)`
	ul {
		padding: 0;
		max-width: 28.375rem;
		li {
			margin: 1.5rem 0;
			list-style: none;
		}
	}
	a {
		text-decoration: none;
	}
`;

export const BackgroundSection = ({
	bgImage,
	id,
	styles,
	className,
	children,
}) => (
	<BackgroundImage
		Tag='section'
		role='img'
		id={id}
		classId={id}
		className={className}
		aria-label={id}
		{...bgImage}
		title=''
		style={styles}
		fadeIn='soft'
	>
		{children}
	</BackgroundImage>
);

export const ItemGrid = ({ children, sx = {} }) => {
	const localSx = {
		backgroundColor: '#EFEFEF',
		border: '1px solid #CECECE',
		borderCollapse: 'collapse',
		p: 0,
		gridGap: '1px',
		...sx,
	};
	return (
		<ItemListContainer>
			<Grid
				columns={[2, 3, 4]}
				mb={5}
				sx={localSx}
			>
				{children}
			</Grid>
		</ItemListContainer>
	);
};

export const ItemGridBox = ({ children, sx = {} }) => {
	const localSx = {
		backgroundColor: 'white',
		border: '1px solid #CECECE',
		borderCollapse: 'collapse',
		overflowWrap: 'break-word',
		p: 2,
		...sx,
		'&:hover': {
			backgroundColor: 'primaryExtraLight',
		},
	};
	return (
		<Flex
			className='itemGridBox'
			sx={localSx}
		>
			{children}
		</Flex>
	);
};

export const SideWrapper = ({
	children,
	styles = {},
	count = 1,
	visible = [true, true, true, true],
}) => {
	const breakpointIndex = useBreakpointIndex();
	const mustHide =
		visible.length > 0 && breakpointIndex < visible.length
			? !visible[breakpointIndex]
			: false;

	if (mustHide || count === 0) {
		return null;
	}
	const localSx = {
		...styles,
	};
	return <Box sx={localSx}>{children}</Box>;
};
